/**
 * App 详情页
 * 包含三个 Tab：基本信息、分类管理、项目发布
 */

import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  Activity,
  ArrowLeft,
  Loader2,
  Package,
  Settings,
  FolderTree,
  Rocket,
  LogOut,
  Shield,
  ImageIcon,
} from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs'
import { useAuthStore } from '@/shared/store/authStore'
import { useToast } from '@/shared/hooks/useToast'
import { AppApiService } from '../services/app-api'
import type { App } from '../types/app'
import AppEditForm from '../components/AppEditForm'
import CategoryList from '../components/CategoryList'
import ProjectPublishList from '../components/ProjectPublishList'

export default function AppDetail() {
  const { appId } = useParams<{ appId: string }>()
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const { showError } = useToast()

  // 状态
  const [app, setApp] = useState<App | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('info')

  // 加载 App 详情
  const loadApp = useCallback(async () => {
    if (!appId) return

    setIsLoading(true)
    try {
      const response = await AppApiService.getApp(appId)
      if (response.success && response.data) {
        setApp(response.data)
      }
    } catch (error: any) {
      console.error('❌ [AppDetail] 加载 App 详情失败:', error)
      if (error.response?.status === 404) {
        showError('App 不存在')
        navigate('/apps')
      } else {
        showError('加载 App 详情失败，请刷新页面重试')
      }
    } finally {
      setIsLoading(false)
    }
  }, [appId, navigate, showError])

  useEffect(() => {
    loadApp()
  }, [loadApp])

  // App 更新回调
  const handleAppUpdate = (updatedApp: App) => {
    setApp(updatedApp)
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    )
  }

  if (!app) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col items-center justify-center gap-4">
        <p className="text-slate-500">App 不存在或已被删除</p>
        <Button onClick={() => navigate('/apps')}>返回 App 列表</Button>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* 顶部导航栏 */}
      <header className="border-b bg-white dark:bg-slate-900 sticky top-0 z-10">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <div className="bg-primary/10 p-2 rounded-lg">
                <Activity className="h-6 w-6 text-primary" />
              </div>
              <span className="font-bold text-xl tracking-tight">Axiom</span>
            </Link>
            <span className="text-slate-300">/</span>
            <Link
              to="/apps"
              className="flex items-center gap-2 text-slate-500 hover:text-slate-700"
            >
              <Package className="h-5 w-5" />
              <span>App 管理</span>
            </Link>
            <span className="text-slate-300">/</span>
            <div className="flex items-center gap-2">
              {app.icon_url ? (
                <img
                  src={app.icon_url}
                  alt={app.app_name}
                  className="h-6 w-6 rounded object-cover"
                />
              ) : (
                <ImageIcon className="h-5 w-5 text-primary" />
              )}
              <span className="font-semibold">{app.app_name}</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/apps')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              返回列表
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 hover:opacity-80 transition-opacity">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.avatar} />
                    <AvatarFallback>
                      {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-2 py-1.5 border-b">
                  <p className="text-sm font-medium">{user?.name || '用户'}</p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                {(user?.role === 'admin' || user?.role === 'super_admin') && (
                  <>
                    <DropdownMenuItem onClick={() => navigate('/admin/users')}>
                      <Shield className="mr-2 h-4 w-4" />
                      用户管理
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem
                  onClick={async () => {
                    try {
                      await logout()
                      navigate('/login')
                    } catch (error) {
                      console.error('退出登录失败:', error)
                      navigate('/login')
                    }
                  }}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  退出登录
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* 主内容区 */}
      <main className="container mx-auto px-4 py-8">
        {/* 页面标题 */}
        <div className="mb-8">
          <div className="flex items-center gap-4">
            {app.icon_url ? (
              <img
                src={app.icon_url}
                alt={app.app_name}
                className="h-16 w-16 rounded-xl object-cover shadow-sm"
              />
            ) : (
              <div className="h-16 w-16 rounded-xl bg-primary/10 flex items-center justify-center shadow-sm">
                <ImageIcon className="h-8 w-8 text-primary" />
              </div>
            )}
            <div>
              <h1 className="text-3xl font-bold tracking-tight">{app.app_name}</h1>
              <p className="text-slate-500 mt-1">
                {app.company || '未设置公司'} · App ID: {app.app_id}
              </p>
            </div>
          </div>
        </div>

        {/* Tab 导航 */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full max-w-md grid-cols-3">
            <TabsTrigger value="info" className="gap-2">
              <Settings className="h-4 w-4" />
              基本信息
            </TabsTrigger>
            <TabsTrigger value="categories" className="gap-2">
              <FolderTree className="h-4 w-4" />
              分类管理
            </TabsTrigger>
            <TabsTrigger value="publish" className="gap-2">
              <Rocket className="h-4 w-4" />
              项目发布
            </TabsTrigger>
          </TabsList>

          {/* 基本信息 Tab */}
          <TabsContent value="info">
            <AppEditForm app={app} onUpdate={handleAppUpdate} />
          </TabsContent>

          {/* 分类管理 Tab */}
          <TabsContent value="categories">
            <CategoryList appId={app.app_id} />
          </TabsContent>

          {/* 项目发布 Tab */}
          <TabsContent value="publish">
            <ProjectPublishList appId={app.app_id} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  )
}
